import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  Package, 
  FolderTree, 
  FileText, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  Settings
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9c04a317-074b-47df-afd3-88339795c9c4/artifacts/rv087trj_logo.jpg";

const AdminLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { href: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { href: '/admin/products', icon: Package, label: 'Produse' },
    { href: '/admin/categories', icon: FolderTree, label: 'Categorii' },
    { href: '/admin/pages', icon: FileText, label: 'Pagini' },
    { href: '/admin/settings', icon: Settings, label: 'Setări' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const isActive = (href) => location.pathname === href;

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside 
        className={`admin-sidebar fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-20'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/admin" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Shagun Surfaces" className="h-10 w-auto" />
              {sidebarOpen && (
                <span className="text-white font-serif text-lg">Admin</span>
              )}
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-3 px-4 py-3 rounded transition-all ${
                  isActive(item.href)
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]'
                    : 'text-[#A3A3A3] hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <item.icon size={20} />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </Link>
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-white/10">
            {sidebarOpen && (
              <div className="mb-4 px-4">
                <p className="text-white text-sm font-medium">{user?.name}</p>
                <p className="text-[#A3A3A3] text-xs">{user?.email}</p>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-[#A3A3A3] hover:bg-red-500/10 hover:text-red-400 rounded transition-all"
              data-testid="admin-logout-btn"
            >
              <LogOut size={20} />
              {sidebarOpen && <span>Deconectare</span>}
            </button>
          </div>

          {/* View Site */}
          <div className="p-4 border-t border-white/10">
            <Link
              to="/"
              target="_blank"
              className="flex items-center gap-3 px-4 py-3 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded transition-all"
            >
              <ChevronRight size={20} />
              {sidebarOpen && <span>Vezi Site</span>}
            </Link>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-[#121212] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-[#A3A3A3] hover:text-white lg:hidden"
              data-testid="toggle-sidebar-btn"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <h1 className="text-white font-serif text-2xl">{title}</h1>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
